import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { parentApi, parentAuth, API } from "../lib/api";
import { NaaviLogo, ShapeFlower } from "../components/brand/Shapes";
import { LogOut, ExternalLink, Download, CalendarDays, CheckCircle2, GraduationCap, Wallet, ChevronRight, ChevronLeft, Newspaper, Receipt, Utensils, CreditCard, Lock, ShieldCheck, X, Sparkles, FileText, Send, MessageSquare, Share2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CalendarPanel from "./admin/CalendarPanel";

const PROGRAM_LABELS = {
  toddlers: "Toddlers (2–3)",
  nursery: "Early Years 1 (3–4)",
  "pre-k": "Early Years 2 (4–5)",
  kindergarten: "Early Years 3 (5–6)",
};

const STATUS_DESC = {
  new: "Your enquiry has been received. Our admissions team will reach out within one working day.",
  contacted: "You've been contacted by our admissions team — we're scheduling next steps.",
  visited: "You've completed a campus visit. We're so glad you came.",
  enrolled: "Welcome to Naavi! Your child is officially enrolled.",
  closed: "This enquiry is closed. Reply to our last email if you'd like to reopen it.",
};

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

async function downloadParentReceipt(rid, no) {
  if (!rid) return;
  try {
    const res = await parentApi.get(`/parent/receipts/${rid}.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `naavi-receipt-${String(no || rid).replace(/\//g, "-")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download receipt. Please try again.");
  }
}

function ageString(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  if (months < 0) { years -= 1; months += 12; }
  return `${years}y ${months}m`;
}

export default function ParentPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [termReports, setTermReports] = useState({ students: [], terms: [], current_session: "" });
  const [loading, setLoading] = useState(true);
  const [activeStudentId, setActiveStudentId] = useState(null);

  useEffect(() => {
    if (!parentAuth.get()) {
      navigate("/parent/login");
      return;
    }
    Promise.all([
      parentApi.get("/parent/me"),
      parentApi.get("/parent/updates").catch(() => ({ data: [] })),
      parentApi.get("/parent/term-reports").catch(() => ({ data: { students: [] } })),
    ])
      .then(([meRes, updRes, trRes]) => {
        setData(meRes.data);
        setUpdates(updRes.data || []);
        setTermReports(trRes.data || { students: [] });
        if (meRes.data.students?.length) setActiveStudentId(meRes.data.students[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const refreshMe = async () => {
    try {
      const { data } = await parentApi.get("/parent/me");
      setData(data);
    } catch {/* ignore */}
  };

  const logout = () => {
    parentAuth.clear();
    navigate("/parent/login");
  };

  const activeStudent = useMemo(() => {
    if (!data?.students) return null;
    return data.students.find((s) => s.id === activeStudentId) || data.students[0];
  }, [data, activeStudentId]);

  if (loading) {
    return <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center text-[#4A5568] text-sm">Loading your portal…</div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center text-[#4A5568] text-sm">No data found.</div>;
  }

  const e = data.enquiry || {};
  const hasChildren = (data.students || []).length > 0;

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="parent-portal">
      <header className="sticky top-0 z-40 bg-[#FDFDF9]/85 backdrop-blur-xl border-b border-[#0F1B47]/8">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <NaaviLogo height={32} />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#648DE5] hidden md:inline">Parent portal</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full hover:bg-[#0F1B47]/5">
              <ExternalLink size={14} /> View site
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47] hover:text-[#FDFDF9]" data-testid="parent-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-10 relative">
        <ShapeFlower size={120} className="absolute top-2 right-2 opacity-50 hidden md:block" color="#C6D947" />

        {hasChildren ? (
          <EnrolledHeader
            students={data.students}
            activeId={activeStudent?.id}
            onSwitch={setActiveStudentId}
          />
        ) : (
          <EnquiryHeader e={e} />
        )}

        {/* Child profile — at the very top, name + photo + class + age */}
        {hasChildren && activeStudent && (
          <StudentProfileCard student={activeStudent} />
        )}

        {/* Weekly updates */}
        <UpdatesFeed updates={updates} />

        {/* Attendance */}
        {hasChildren && activeStudent && (
          <AttendanceCard student={activeStudent} />
        )}

        {/* Enquiry tracker (only for non-enrolled enquiries) */}
        {!!e.id && e.status !== "enrolled" && (
          <EnquiryTracker e={e} />
        )}

        {/* Visits */}
        <VisitsBlock visits={data.visits || []} />

        {/* Weekly food menu */}
        <FoodMenuCard />

        {/* School calendar PDF — annual */}
        <SchoolCalendarPdfCard />

        {/* Events & holidays — month-by-month read-only list */}
        <div className="mt-8" data-testid="parent-calendar-section">
          <CalendarPanel readOnly />
        </div>

        {/* Monthly newsletter — before the fee section */}
        <NewsletterCard />

        {/* Leave requests — send a leave note to the school */}
        {hasChildren && <LeaveRequestCard students={data.students || []} />}

        {/* Fee ledger — last */}
        {hasChildren && activeStudent && <LedgerCard student={activeStudent} onPaid={refreshMe} />}
        {hasChildren && activeStudent?.receipts?.length > 0 && <ReceiptsCard student={activeStudent} />}
        {hasChildren && activeStudent && <TermReportsCard student={activeStudent} termReports={termReports} />}
      </main>
    </div>
  );
}

function EnrolledHeader({ students, activeId, onSwitch }) {
  const greetingName = (students[0]?.father?.name || students[0]?.mother?.name || "").split(" ")[0] || "friend";
  return (
    <div className="mb-8">
      <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-3 block">Welcome</span>
      <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0F1B47] leading-tight">
        Hello, <span className="italic font-normal">{greetingName}</span>.
      </h1>
      <p className="text-[#4A5568] text-base font-light mt-3 max-w-2xl">
        {students.length === 1
          ? `Here's everything for ${students[0].full_name} — class, fee ledger and upcoming visits.`
          : "Switch between your children below to see each one's class, fee ledger and visits."}
      </p>

      {students.length > 1 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="parent-child-switcher">
          {students.map((s) => (
            <button
              key={s.id}
              onClick={() => onSwitch(s.id)}
              className={`text-sm font-semibold rounded-full px-4 py-2 border transition-colors ${
                s.id === activeId
                  ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                  : "bg-[#FDFDF9] text-[#0F1B47] border-[#0F1B47]/15 hover:border-[#0F1B47]/40"
              }`}
            >
              {s.full_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EnquiryHeader({ e }) {
  return (
    <div className="mb-10">
      <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-3 block">Your enquiry</span>
      <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0F1B47] leading-tight">
        Hello, <span className="italic font-normal">{e.father_name?.split(" ")[0] || "friend"} & {e.mother_name?.split(" ")[0] || "family"}</span>.
      </h1>
      <p className="text-[#4A5568] text-base font-light mt-4 max-w-2xl">
        Here's where things stand for <b>{e.student_name}</b>. We'll keep this page in sync with our team's progress.
      </p>
    </div>
  );
}

function EnquiryTracker({ e }) {
  const status = e.status || "new";
  const stages = ["new", "contacted", "visited", "enrolled"];
  const stageIdx = Math.max(0, stages.indexOf(status));
  return (
    <div className="bg-[#FDFDF9] rounded-3xl p-7 md:p-9 border border-[#0F1B47]/8 mb-6" data-testid="parent-status-card">
      <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5] mb-2">Application status</div>
      <div className="font-display text-2xl text-[#0F1B47] font-medium capitalize">{status}</div>
      <p className="text-[#4A5568] text-sm font-light mt-2 max-w-2xl">{STATUS_DESC[status] || ""}</p>
      <div className="mt-7 flex items-center gap-2">
        {stages.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex-1 flex flex-col items-center text-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${i <= stageIdx ? "bg-[#0F1B47] text-[#FDFDF9]" : "bg-[#F5F5F0] text-[#0F1B47]/40 border border-[#0F1B47]/15"}`}>
                {i <= stageIdx ? <CheckCircle2 size={16} /> : <span className="text-xs font-bold">{i + 1}</span>}
              </div>
              <div className={`text-[10px] font-bold tracking-[0.16em] uppercase ${i <= stageIdx ? "text-[#0F1B47]" : "text-[#718096]"}`}>{s}</div>
            </div>
            {i < stages.length - 1 && (
              <div className={`flex-1 h-[2px] rounded-full ${i < stageIdx ? "bg-[#0F1B47]" : "bg-[#0F1B47]/15"}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function StudentProfileCard({ student }) {
  return (
    <div className="bg-[#FDFDF9] rounded-3xl p-6 md:p-8 border border-[#0F1B47]/8 mb-5" data-testid="parent-profile-card">
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <Avatar url={student.photo_url} name={student.full_name} size={72} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-mono text-[#718096] tracking-wide">{student.admission_id}</div>
          <div className="font-display text-2xl text-[#0F1B47] font-semibold mt-0.5">{student.full_name}</div>
          <div className="text-sm text-[#4A5568] mt-1">
            <span className="inline-flex items-center gap-1.5">
              <GraduationCap size={13} /> {student.class}
            </span>
            {" · "}
            {ageString(student.dob)}
            {student.blood_group && (
              <>
                {" · "}
                <span className="font-semibold text-[#0F1B47]">{student.blood_group}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AttendanceCard({ student }) {
  const att = student.attendance_counts || {};
  const totalMarked = (att.present || 0) + (att.absent || 0) + (att.late || 0);
  if (totalMarked === 0) return null;
  const presentPct = Math.round(((att.present || 0) + (att.late || 0) * 0.5) / totalMarked * 100);
  return (
    <div className="bg-[#FDFDF9] rounded-3xl p-6 md:p-7 border border-[#0F1B47]/8 mb-6" data-testid="parent-attendance">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Attendance · last 60 days</div>
          <div className="font-display text-xl text-[#0F1B47] font-medium mt-1">
            {presentPct}% attendance
          </div>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2">
        <AttStat label="Present" value={att.present || 0} cls="bg-emerald-50 text-emerald-700" />
        <AttStat label="Absent" value={att.absent || 0} cls="bg-rose-50 text-rose-700" />
        <AttStat label="Late" value={att.late || 0} cls="bg-amber-50 text-amber-700" />
        <AttStat label="Leave" value={att.leave || 0} cls="bg-sky-50 text-sky-700" />
        <AttStat label="Holiday" value={att.holiday || 0} cls="bg-violet-50 text-violet-700" />
      </div>
    </div>
  );
}

function LedgerCard({ student, onPaid }) {
  const balance = student.balance || 0;
  const invoices = student.invoices || [];
  const outstanding = invoices.filter((i) => (i.outstanding || 0) > 0);
  const [payInvoice, setPayInvoice] = useState(null);

  return (
    <div className="space-y-6">
      {outstanding.length > 0 && (
        <div className="bg-[#FDFDF9] rounded-3xl p-6 md:p-8 border border-[#0F1B47]/8" data-testid="parent-invoices">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5] flex items-center gap-1.5">
                <CreditCard size={12} /> Invoices to pay
              </div>
              <div className="font-display text-xl text-[#0F1B47] font-medium mt-1">
                {outstanding.length} pending {outstanding.length === 1 ? "invoice" : "invoices"} · {INR(Math.max(balance, 0))}
              </div>
            </div>
          </div>
          <ul className="space-y-3">
            {outstanding.map((inv) => (
              <li key={inv.id} className="bg-[#FFE9C7]/60 border border-[#a06400]/15 rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`parent-invoice-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0F1B47]">{inv.period || "Fee due"}</div>
                  <div className="text-xs text-[#4A5568] mt-1">
                    Issued {inv.date}
                    {inv.paid_against > 0 && ` · part-paid ${INR(inv.paid_against)} of ${INR(inv.amount)}`}
                  </div>
                  {inv.note && <div className="text-[11px] text-[#718096] italic mt-1">"{inv.note}"</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#a06400]">Outstanding</div>
                  <div className="font-display text-lg font-semibold text-[#0F1B47]">{INR(inv.outstanding)}</div>
                </div>
                <button
                  onClick={() => setPayInvoice(inv)}
                  className="inline-flex items-center justify-center gap-2 bg-[#C6D947] text-[#0F1B47] rounded-full px-5 py-2.5 font-semibold text-sm hover:-translate-y-0.5 transition-transform shrink-0"
                  data-testid={`parent-invoice-pay-${inv.id}`}
                >
                  <CreditCard size={14} /> Pay {INR(inv.outstanding)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-[#FDFDF9] rounded-3xl p-6 md:p-8 border border-[#0F1B47]/8" data-testid="parent-ledger">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5] flex items-center gap-1.5">
              <Wallet size={12} /> Fee ledger
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <SummaryStat label="Total paid" value={INR(student.total_paid)} tone="teal" />
          <SummaryStat
            label="Pending fee"
            value={INR(outstanding.reduce((s, i) => s + (i.outstanding || 0), 0))}
            tone={outstanding.length > 0 ? "amber" : "green"}
          />
        </div>

        {student.entries?.length === 0 ? (
          <p className="text-sm text-[#4A5568] font-light text-center py-6">No fee entries yet. Your school will record fees here once they're generated.</p>
        ) : (
          <div className="bg-[#F5F5F0] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0F1B47]/5">
                <tr className="text-left text-[10px] font-bold tracking-[0.16em] uppercase text-[#0F1B47]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Fee</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {(student.entries || []).map((e) => (
                  <tr key={e.id} className="border-t border-[#0F1B47]/8">
                    <td className="px-4 py-3 whitespace-nowrap text-[#0F1B47]">{e.date}</td>
                    <td className="px-4 py-3 text-[#0F1B47]">
                      {e.type === "generated" ? `Invoice · ${e.period}`
                        : e.type === "paid" ? `Payment · ${e.period}`
                        : `Adjustment · ${e.period}`}
                      {e.payment_mode && <div className="text-[10px] text-[#718096] uppercase tracking-wide">{e.payment_mode}{e.ref_no ? ` · ${e.ref_no}` : ""}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {e.type === "generated" ? <span className="text-rose-600 font-semibold">{INR(e.amount)}</span> : <span className="text-[#718096]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {e.type === "paid" ? <span className="text-teal-700 font-semibold">{INR(e.amount)}</span> : <span className="text-[#718096]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {payInvoice && (
        <PayNowModal
          student={student}
          invoice={payInvoice}
          onClose={() => setPayInvoice(null)}
          onSuccess={() => { setPayInvoice(null); onPaid?.(); }}
        />
      )}
    </div>
  );
}

function PayNowModal({ student, invoice, onClose, onSuccess }) {
  const cap = Math.max(invoice ? (invoice.outstanding || 0) : (student.balance || 0), 0);
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!cap || cap <= 0) return toast.error("There's nothing pending to pay");
    setPaying(true);
    try {
      // The amount is decided server-side from the invoice/balance — never sent
      // from the browser, so it can't be tampered with in transit.
      const body = { student_id: student.id };
      if (invoice) body.invoice_id = invoice.id;
      // Which gateway is live? HDFC SmartGateway redirects; Razorpay opens a popup.
      const { data: cfg } = await parentApi.get("/payments/config");
      if (cfg?.gateway === "hdfc") {
        const { data: start } = await parentApi.post("/parent/payments/hdfc/start/fees", body);
        if (!start?.payment_link) throw new Error("No payment link returned");
        // Full-page redirect to HDFC's hosted payment page; parent returns to /payment-status.
        window.location.href = start.payment_link;
        return;
      }
      // ---- Razorpay (on-site popup) ----
      if (typeof window === "undefined" || !window.Razorpay) {
        setPaying(false);
        return toast.error("Payment gateway is still loading. Please try again in a moment.");
      }
      const { data: order } = await parentApi.post("/parent/fees/razorpay-order", body);
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: "Naavi Early Years",
        description: invoice ? `Invoice · ${invoice.period}` : `Fees · ${student.full_name}`,
        prefill: order.prefill,
        theme: { color: "#0F1B47" },
        handler: async (resp) => {
          try {
            const { data } = await parentApi.post("/parent/fees/razorpay-verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              student_id: student.id,
            });
            setSuccess(data);
            toast.success(`Payment of ${INR(data.amount)} successful.`);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed. Please contact the school.");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info?.("Payment cancelled.");
          },
        },
      });
      rzp.on?.("payment.failed", (resp) => {
        const reason = resp?.error?.description || "Payment failed. Please try again.";
        toast.error(reason);
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not initiate payment. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-[28px] w-full max-w-md shadow-2xl overflow-hidden" data-testid="pay-now-modal">
        {success ? (
          <div className="p-7 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#5BC4B8] text-[#0F1B47] flex items-center justify-center mb-4">
              <CheckCircle2 size={26} />
            </div>
            <h3 className="font-display text-2xl text-[#0F1B47] font-medium">Payment received.</h3>
            <p className="text-sm text-[#4A5568] mt-2 font-light">
              Thank you. We've recorded a payment of <b className="text-[#0F1B47]">{INR(success.amount)}</b>{invoice ? <> against the <b className="text-[#0F1B47]">{invoice.period}</b> invoice</> : null}.
            </p>
            <div className="mt-5 inline-flex flex-col items-start bg-[#F5F5F0] rounded-2xl px-5 py-3 text-left">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Transaction ref</div>
              <div className="font-mono text-sm font-semibold text-[#0F1B47]">{success.transaction_ref}</div>
              <div className="text-[11px] text-[#4A5568] mt-1">Receipt: <b className="text-[#0F1B47] font-mono">{success.receipt_no}</b></div>
              <div className="text-[11px] text-[#4A5568] mt-1">New balance: <b className="text-[#0F1B47]">{INR(Math.max(success.balance || 0, 0))}</b></div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => downloadParentReceipt(success.receipt_id, success.receipt_no)}
                className="inline-flex items-center justify-center gap-1.5 bg-[#C6D947] text-[#0F1B47] rounded-full py-3 font-semibold text-sm hover:-translate-y-0.5 transition-transform"
                data-testid="pay-success-download"
              >
                <Download size={14} /> Download receipt
              </button>
              <button onClick={onSuccess} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 font-semibold text-sm hover:bg-[#1a2b6b]" data-testid="pay-success-close">
                Done
              </button>
            </div>
            <p className="text-[11px] text-[#718096] mt-3 italic">Payment processed securely via Razorpay.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="bg-[#0F1B47] text-[#FDFDF9] px-7 py-6 relative">
              <button type="button" onClick={onClose} className="absolute right-4 top-4 p-1.5 rounded-full hover:bg-white/10" aria-label="Close">
                <X size={16} />
              </button>
              <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C6D947] mb-1.5">{invoice ? "Pay invoice" : "Pay fees"}</div>
              <div className="font-display text-2xl font-medium leading-tight">{invoice ? invoice.period : student.full_name}</div>
              <div className="text-xs text-[#FDFDF9]/70 mt-1">
                {invoice
                  ? <>Invoice for {student.full_name} · outstanding {INR(cap)}</>
                  : <>Outstanding balance · {INR(cap)}</>}
              </div>
            </div>
            <div className="p-7 space-y-5">
              <div className="block">
                <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-2 block">Amount to pay</span>
                <div className="flex items-center justify-between bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-3.5">
                  <span className="text-2xl font-semibold text-[#0F1B47]" data-testid="pay-amount-fixed">{INR(cap)}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#4A5568] bg-[#0F1B47]/6 rounded-full px-2.5 py-1"><Lock size={10} /> Set by school</span>
                </div>
                <p className="text-[11px] text-[#718096] mt-2">
                  This is the {invoice ? `amount due for the ${invoice.period} invoice` : "outstanding balance"}. The amount is fixed by the school and can't be changed.
                </p>
              </div>
              <div className="bg-[#FAFAF5] rounded-2xl p-4 border border-[#0F1B47]/8">
                <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-1.5"><Lock size={11} /> Secure checkout</div>
                <ul className="text-[12px] text-[#0F1B47]/80 space-y-1.5">
                  <li className="flex items-start gap-2"><Sparkles size={11} className="mt-0.5 text-[#5BC4B8] flex-shrink-0" /><span>Instant confirmation in your portal</span></li>
                  <li className="flex items-start gap-2"><ShieldCheck size={11} className="mt-0.5 text-[#5BC4B8] flex-shrink-0" /><span>Receipt available for download once issued</span></li>
                </ul>
              </div>
              <button
                type="submit"
                disabled={paying}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C6D947] text-[#0F1B47] rounded-full py-4 font-semibold text-[15px] hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                data-testid="pay-confirm-btn"
              >
                {paying ? (
                  <>
                    <span className="w-3 h-3 border-2 border-[#0F1B47] border-t-transparent rounded-full animate-spin" />
                    Processing payment…
                  </>
                ) : (
                  <>
                    <CreditCard size={16} /> Pay {INR(cap)}
                  </>
                )}
              </button>
              <p className="text-[11px] text-[#718096] text-center italic">
                Demo mode — payment is simulated for testing. No real charge.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ReceiptsCard({ student }) {
  const receipts = student.receipts || [];
  const downloadPdf = (rid, no) => downloadParentReceipt(rid, no);
  return (
    <div className="bg-[#FDFDF9] rounded-3xl p-6 md:p-8 border border-[#0F1B47]/8 mt-6" data-testid="parent-receipts">
      <div className="flex items-center gap-2 mb-1">
        <Receipt size={14} className="text-[#648DE5]" />
        <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Receipts</div>
      </div>
      <div className="font-display text-2xl text-[#0F1B47] font-medium">Fee receipts</div>
      <p className="text-sm text-[#4A5568] mt-1 mb-5">Auto-generated PDFs for each payment, ready to download.</p>
      <ul className="space-y-3">
        {receipts.map((r) => (
          <li key={r.id} className="bg-[#F5F5F0] rounded-2xl p-4 flex items-center gap-4" data-testid={`parent-receipt-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[12px] text-[#0F1B47] font-semibold">{r.receipt_no}</div>
              <div className="text-sm text-[#0F1B47] mt-0.5">{r.period}</div>
              <div className="text-xs text-[#4A5568] mt-0.5">{r.payment_mode} · paid on {r.payment_date}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-lg text-[#0F1B47] font-semibold">{INR(r.total)}</div>
            </div>
            <button
              onClick={() => downloadPdf(r.id, r.receipt_no)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1B47] px-3.5 py-2 rounded-full bg-[#E3EDF9] hover:bg-[#CFE0F0]"
              data-testid={`parent-receipt-download-${r.id}`}
            >
              <Download size={12} /> PDF
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TermReportsCard({ student, termReports }) {
  const myBucket = (termReports?.students || []).find((s) => s.student_id === student.id);
  const sessions = myBucket?.sessions || [];
  const terms = termReports?.terms || ["Term 1", "Term 2", "Term 3"];
  const currentSession = termReports?.current_session;
  const openPdf = async (rid) => {
    try {
      const res = await parentApi.get(`/parent/term-reports/${rid}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open PDF");
    }
  };
  const current = sessions.find((s) => s.session === currentSession);
  const currentReportsByTerm = new Map((current?.reports || []).map((r) => [r.term, r]));
  const currentRows = terms.map((t) => currentReportsByTerm.get(t) || { term: t, _placeholder: true });
  const pastSessions = sessions.filter((s) => s.session !== currentSession);

  return (
    <div className="bg-[#FDFDF9] rounded-3xl p-6 md:p-8 border border-[#0F1B47]/8 mt-6" data-testid={`parent-term-reports-${student.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <FileText size={14} className="text-[#648DE5]" />
        <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Reports</div>
      </div>
      <div className="font-display text-2xl text-[#0F1B47] font-medium">Term reports</div>
      <p className="text-sm text-[#4A5568] mt-1 mb-5">Downloadable PDF report card each term.</p>

      {currentSession && (
        <div className="mb-4">
          <div className="font-display text-lg text-[#0F1B47] font-medium mb-2">Session {currentSession}</div>
          <div className="space-y-2">
            {currentRows.map((r) => (
              <div key={r.id || r.term} className={`flex items-center gap-3 rounded-2xl p-3 ${r._placeholder ? "border border-dashed border-[#0F1B47]/15 bg-transparent" : "bg-[#F5F5F0]"}`} data-testid={r.id ? `parent-term-report-${r.id}` : `parent-term-report-placeholder-${r.term}`}>
                <div className={`w-9 h-11 rounded grid place-items-center flex-shrink-0 text-white text-[10px] font-bold ${r._placeholder ? "bg-[#E2E8F0] text-[#A0AEC0]" : "bg-gradient-to-b from-[#FED7D7] to-[#F97878]"}`}>
                  {r._placeholder ? "—" : "PDF"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-bold text-sm ${r._placeholder ? "text-[#718096]" : "text-[#0F1B47]"}`}>{r.term}</div>
                  <div className="text-[11px] text-[#718096]">
                    {r._placeholder ? "Coming soon" : `Published ${new Date(r.uploaded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · ${(r.size_bytes / 1024).toFixed(0)} KB`}
                  </div>
                  {r.notes && <div className="text-[11px] text-[#4A5568] italic mt-0.5">{r.notes}</div>}
                </div>
                {r._placeholder ? (
                  <span className="text-[10px] text-[#A0AEC0] font-semibold">Not yet</span>
                ) : (
                  <button
                    onClick={() => openPdf(r.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0F1B47] px-3.5 py-2 rounded-full bg-[#C6D947] hover:brightness-95"
                    data-testid={`parent-term-report-view-${r.id}`}
                  >
                    <ExternalLink size={12} /> View
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {pastSessions.map((s) => (
        <div key={s.session} className="mb-4 opacity-80">
          <div className="font-display text-base text-[#4A5568] font-medium mb-2">Session {s.session}</div>
          <div className="space-y-2">
            {s.reports.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-2xl p-3 bg-transparent border border-[#0F1B47]/8" data-testid={`parent-term-report-${r.id}`}>
                <div className="w-9 h-11 rounded grid place-items-center flex-shrink-0 text-white text-[10px] font-bold bg-gradient-to-b from-[#BEE3F8] to-[#4299E1]">PDF</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#0F1B47]">{r.term}</div>
                  <div className="text-[11px] text-[#718096]">Published {new Date(r.uploaded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
                </div>
                <button
                  onClick={() => openPdf(r.id)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1B47] px-3.5 py-2 rounded-full bg-[#E3EDF9] hover:bg-[#CFE0F0]"
                  data-testid={`parent-term-report-view-${r.id}`}
                >
                  <ExternalLink size={12} /> View
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {sessions.length === 0 && (
        <div className="text-center text-[#A0AEC0] italic text-sm py-6">No term reports yet.</div>
      )}
    </div>
  );
}


function UpdatesFeed({ updates }) {
  if (!updates || updates.length === 0) return null;
  const classPosts = updates.filter((u) => u.audience !== "all");
  const eventPosts = updates.filter((u) => u.audience === "all");
  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 mb-6 overflow-hidden" data-testid="parent-updates-feed">
      <div className="bg-[#0F1B47] text-[#FDFDF9] px-5 md:px-9 py-5 flex items-start gap-3">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-[#C6D947]/18 text-[#C6D947] flex items-center justify-center flex-shrink-0">
          <Newspaper size={18} />
        </div>
        <div>
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C6D947]">Weekly updates</div>
          <div className="font-display text-xl md:text-2xl font-medium mt-0.5 leading-tight">What's happening this week</div>
          <div className="text-xs md:text-[13px] text-[#FDFDF9]/70 mt-1">Class notes for your kids and events for the whole school.</div>
        </div>
      </div>

      <UpdatesAlbum title="Class notes" emptyText="No class notes yet for your child's class." posts={classPosts} />
      <UpdatesAlbum title="📣 Events" emptyText="No school-wide events yet." posts={eventPosts} eventStyle />
    </div>
  );
}

function UpdatesAlbum({ title, emptyText, posts, eventStyle }) {
  const [active, setActive] = useState(null);
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between px-4 md:px-9 pb-2">
        <div className="font-display text-base md:text-lg text-[#0F1B47] font-medium">{title}</div>
        {posts.length > 1 && <div className="text-[11px] text-[#718096] font-semibold">Swipe →</div>}
      </div>
      {posts.length === 0 ? (
        <div className="mx-4 md:mx-9 mb-4 text-[12px] text-[#A0AEC0] italic">{emptyText}</div>
      ) : (
        <div className="flex gap-3 md:gap-4 overflow-x-auto px-4 md:px-9 pb-5 snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }}>
          {posts.map((u) => <UpdatePostCard key={u.id} u={u} eventStyle={eventStyle} onOpen={() => setActive(u)} />)}
        </div>
      )}
      {active && <UpdateModal u={active} eventStyle={eventStyle} onClose={() => setActive(null)} />}
    </div>
  );
}

// Truncate to N words; returns {text, truncated}
function truncateWords(s, n = 100) {
  if (!s) return { text: "", truncated: false };
  const words = s.trim().split(/\s+/);
  if (words.length <= n) return { text: s, truncated: false };
  return { text: words.slice(0, n).join(" ") + "…", truncated: true };
}

function UpdatePostCard({ u, eventStyle, onOpen }) {
  const wrap = eventStyle
    ? "bg-gradient-to-br from-[#0F1B47] to-[#1f2d5a] text-[#FDFDF9]"
    : "bg-[#F5F5F0] text-[#0F1B47]";
  const headlineCls = eventStyle ? "text-[#FDFDF9]" : "text-[#0F1B47]";
  const bodyCls = eventStyle ? "text-[#FDFDF9]/85" : "text-[#4A5568]";
  const metaCls = eventStyle ? "text-[#FDFDF9]/65" : "text-[#718096]";
  const pillCls = eventStyle
    ? "bg-[#C6D947] text-[#0F1B47]"
    : "bg-[#D5DDFF] text-[#1a2b6b]";
  const classes = (u.classes || []).join(", ") || (u.audience === "all" ? "All classes" : "Class");
  const { text: bodyShort, truncated } = truncateWords(u.body || "", 100);
  const photos = u.photos || [];
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex-none w-[78%] sm:w-[55%] lg:w-[40%] h-[460px] snap-start ${wrap} rounded-3xl p-4 md:p-5 shadow-sm text-left flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all`}
      data-testid={`parent-update-${u.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <span className={`text-[9px] font-extrabold tracking-[0.16em] uppercase rounded-full px-2.5 py-1 ${pillCls}`}>
          {u.audience === "all" ? "📣 All classes" : classes}
        </span>
        <span className={`text-[10px] ${metaCls}`}>{u.week_label || new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
      </div>
      <div className={`font-display text-[16px] md:text-[18px] font-medium leading-tight mb-1.5 line-clamp-2 ${headlineCls}`}>{u.title}</div>
      {bodyShort && (
        <p className={`text-[12.5px] leading-[1.5] ${bodyCls} mb-2 whitespace-pre-wrap flex-1 overflow-hidden`}>
          {bodyShort}
        </p>
      )}
      <div className="mt-auto flex items-center justify-between gap-2 pt-2">
        {truncated ? (
          <span
            className={`text-[11px] font-bold inline-flex items-center gap-1 rounded-full px-3 py-1.5 ${eventStyle ? "bg-[#C6D947] text-[#0F1B47]" : "bg-[#C6D947] text-[#0F1B47]"}`}
            data-testid={`parent-update-readmore-${u.id}`}
          >
            Read more →
          </span>
        ) : (
          <span className={`text-[10px] ${metaCls}`}>{photos.length === 0 ? "" : ""}</span>
        )}
        {photos.length > 0 ? (
          <div className="flex items-center gap-1.5">
            {photos.slice(0, 2).map((p) => (
              <ParentAuthImage
                key={p.id}
                path={`/parent/updates/${u.id}/photos/${p.id}`}
                className="w-11 h-11 rounded-lg object-cover bg-[#FDFDF9]/30 shadow-sm"
              />
            ))}
            {photos.length > 2 && (
              <span className={`text-[11px] font-semibold ${metaCls}`}>+{photos.length - 2}</span>
            )}
          </div>
        ) : (
          <span className={`text-[10px] ${metaCls}`}>No photos</span>
        )}
      </div>
    </button>
  );
}

function UpdateModal({ u, eventStyle, onClose }) {
  const classes = (u.classes || []).join(", ") || (u.audience === "all" ? "All classes" : "Class");
  const photos = u.photos || [];
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && lightboxIdx === null) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, lightboxIdx]);
  const slug = (u.title || "update").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F1B47]/55 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
      data-testid={`parent-update-modal-${u.id}`}
    >
      <div
        className="bg-[#FDFDF9] text-[#0F1B47] rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#FDFDF9]/95 backdrop-blur px-6 md:px-8 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-[#0F1B47]/8">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] font-extrabold tracking-[0.16em] uppercase rounded-full px-2.5 py-1 bg-[#D5DDFF] text-[#1a2b6b]">
                {u.audience === "all" ? "📣 All classes" : classes}
              </span>
              <span className="text-[10px] text-[#718096]">{u.week_label || new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#0F1B47] p-1.5 -mr-1 -mt-1 rounded-full hover:bg-[#0F1B47]/5"
            aria-label="Close"
            data-testid="parent-update-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 md:px-8 py-5">
          <h2 className="font-display text-[24px] md:text-[28px] font-medium leading-tight text-[#0F1B47] mb-3">{u.title}</h2>
          {u.body && (
            <p className="text-[14.5px] leading-[1.65] text-[#2D3748] whitespace-pre-wrap">{u.body}</p>
          )}
          {photos.length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] font-semibold text-[#718096] mb-2">
                ← Swipe through · Tap any photo to open
              </div>
              <div
                className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory"
                style={{ scrollbarWidth: "thin" }}
                data-testid="parent-update-modal-gallery"
              >
                {photos.map((p, idx) => (
                  <GalleryThumb
                    key={p.id}
                    path={`/parent/updates/${u.id}/photos/${p.id}`}
                    counter={`${idx + 1} / ${photos.length}`}
                    onOpen={() => setLightboxIdx(idx)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {lightboxIdx !== null && (
        <PhotoLightbox
          photos={photos}
          updateId={u.id}
          startIdx={lightboxIdx}
          slug={slug}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

// Thumbnail in the modal — fetches once, lazy-loads, opens the lightbox on tap.
function GalleryThumb({ path, counter, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    parentApi.get(path, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative flex-none w-[32%] aspect-square rounded-xl overflow-hidden bg-[#0F1B47]/8 snap-start active:scale-[0.98] transition-transform"
      data-testid="parent-update-photo-thumb"
      title="Tap to open"
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
      ) : (
        <div className="w-full h-full animate-pulse bg-[#0F1B47]/10" />
      )}
      <span className="absolute left-1.5 top-1.5 bg-[#0F1B47]/72 text-[#FDFDF9] text-[10px] font-bold tracking-wider rounded-full px-2 py-0.5 backdrop-blur">
        {counter}
      </span>
    </button>
  );
}

// Full-screen photo viewer with horizontal swipe between slides + per-slide download/share.
function PhotoLightbox({ photos, updateId, startIdx, slug, onClose }) {
  const scrollerRef = useRef(null);
  const [idx, setIdx] = useState(startIdx);
  // Cache fetched blobs by photo id so download/share doesn't re-hit the network.
  const [blobs, setBlobs] = useState({});

  // Lock body scroll while lightbox is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Jump to the initial slide on mount.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: startIdx * el.clientWidth, behavior: "instant" in el ? "instant" : "auto" });
  }, [startIdx]);

  // Track current slide as the user swipes.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = Math.round(el.scrollLeft / el.clientWidth);
        if (next !== idx) setIdx(next);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [idx]);

  // Keyboard: arrows + Esc.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goTo(idx + 1);
      if (e.key === "ArrowLeft") goTo(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, onClose]);

  const goTo = (n) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(photos.length - 1, n));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  const currentFilename = `naavi-${slug}-${idx + 1}.jpg`;

  const onDownload = async () => {
    const p = photos[idx];
    const blob = blobs[p.id];
    if (!blob) return toast.error("Photo still loading…");
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = currentFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
    toast.success("Photo downloaded");
  };

  const onShare = async () => {
    const p = photos[idx];
    const blob = blobs[p.id];
    if (!blob) return toast.error("Photo still loading…");
    const file = new File([blob], currentFilename, { type: blob.type || "image/jpeg" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Naavi update" });
      } catch (e) {
        // user cancelled — silent
      }
    } else {
      // Fallback: download
      onDownload();
      toast.info("Sharing isn't supported here — downloaded instead.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0F1B47]/95 flex flex-col"
      onClick={(e) => e.stopPropagation()}
      data-testid="parent-photo-lightbox"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 md:px-6 pt-4 pb-2 text-[#FDFDF9] flex-none">
        <span className="text-[12px] font-bold tracking-[0.16em] uppercase" data-testid="parent-photo-lightbox-counter">
          {idx + 1} of {photos.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10"
          aria-label="Close"
          data-testid="parent-photo-lightbox-close"
        >
          <X size={22} />
        </button>
      </div>

      {/* Slides */}
      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollerRef}
          className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory"
          style={{ scrollbarWidth: "none" }}
          data-testid="parent-photo-lightbox-scroller"
        >
          {photos.map((p, i) => (
            <LightboxSlide
              key={p.id}
              path={`/parent/updates/${updateId}/photos/${p.id}`}
              active={i === idx}
              onBlob={(b) => setBlobs((prev) => prev[p.id] ? prev : { ...prev, [p.id]: b })}
            />
          ))}
        </div>

        {/* Desktop arrows */}
        {photos.length > 1 && idx > 0 && (
          <button
            type="button"
            onClick={() => goTo(idx - 1)}
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-[#FDFDF9] backdrop-blur"
            aria-label="Previous photo"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        {photos.length > 1 && idx < photos.length - 1 && (
          <button
            type="button"
            onClick={() => goTo(idx + 1)}
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-[#FDFDF9] backdrop-blur"
            aria-label="Next photo"
          >
            <ChevronRight size={22} />
          </button>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="flex items-center justify-center gap-3 px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] flex-none">
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-2 bg-[#C6D947] text-[#0F1B47] rounded-full px-5 py-2.5 text-[13px] font-bold shadow-lg hover:brightness-95 active:scale-[0.97] transition"
          data-testid="parent-photo-lightbox-download"
        >
          <Download size={15} /> Download
        </button>
        <button
          type="button"
          onClick={onShare}
          className="inline-flex items-center gap-2 bg-white/15 text-[#FDFDF9] rounded-full px-5 py-2.5 text-[13px] font-bold border border-white/20 hover:bg-white/25 active:scale-[0.97] transition backdrop-blur"
          data-testid="parent-photo-lightbox-share"
        >
          <Share2 size={15} /> Share
        </button>
      </div>
    </div>
  );
}

function LightboxSlide({ path, active, onBlob }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!active && src) return; // Only fetch lazily; if already loaded, keep it.
    let url;
    let alive = true;
    parentApi.get(path, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
        onBlob(res.data);
      })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path, active]);
  return (
    <div className="flex-none w-full h-full snap-start flex items-center justify-center px-3 md:px-12">
      {src ? (
        <img src={src} alt="" className="max-w-full max-h-full object-contain rounded-lg select-none" draggable={false} />
      ) : (
        <div className="w-2/3 max-w-md aspect-[4/3] rounded-xl bg-white/10 animate-pulse" />
      )}
    </div>
  );
}

function ParentAuthImage({ path, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    parentApi.get(path, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className={`${className} animate-pulse`} />;
  return <img src={src} alt="" className={className} loading="lazy" decoding="async" />;
}

function VisitsBlock({ visits }) {
  if (visits.length === 0) return null;
  return (
    <div className="bg-[#FDFDF9] rounded-3xl p-7 border border-[#0F1B47]/8 mb-6" data-testid="parent-visits">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Campus visits</div>
          <div className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">Your scheduled tours</div>
        </div>
        <Link to="/#schedule-visit" className="text-sm font-semibold text-[#0F1B47] underline underline-offset-2">
          Schedule another <ChevronRight className="inline" size={14} />
        </Link>
      </div>
      <ul className="space-y-3">
        {visits.map((v) => (
          <li key={v.id} className="flex items-center gap-4 bg-[#F5F5F0] rounded-2xl px-5 py-4">
            <div className="w-10 h-10 rounded-full bg-[#FDFDF9] flex items-center justify-center text-[#648DE5]">
              <CalendarDays size={18} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0F1B47]">{v.visit_date} · {v.time_slot}</div>
              <div className="text-xs text-[#4A5568]">{v.notes || "Guided tour booked"}</div>
            </div>
            <span className="text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2.5 py-1 border bg-[#5BC4B8]/15 text-[#0F1B47] border-[#5BC4B8]/40">
              {v.status || "confirmed"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const MENU_SLOT_DEFS = [
  { id: "mid_morning", label: "Mid-morning snack", dot: "#648DE5" },
  { id: "lunch", label: "Lunch", dot: "#5BC4B8" },
  { id: "evening", label: "Evening snack", dot: "#C6D947" },
];
const MENU_DAY_DEFS = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
];

const padMenu = (n) => String(n).padStart(2, "0");
const isoFromDate = (d) => `${d.getFullYear()}-${padMenu(d.getMonth() + 1)}-${padMenu(d.getDate())}`;
const mondayOfMenu = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - ((dow + 6) % 7));
  return isoFromDate(dt);
};
const addDaysMenu = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return isoFromDate(dt);
};
const friendlyMenu = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

function FoodMenuCard() {
  const todayIso = isoFromDate(new Date());
  const [weekOf, setWeekOf] = useState(mondayOfMenu(todayIso));
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/food-menu/week`, { params: { week_of: weekOf } })
      .then(({ data }) => { if (!cancelled) setMenu(data); })
      .catch(() => { if (!cancelled) setMenu(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekOf]);

  const slots = (menu?.slots && menu.slots.length ? menu.slots : []).filter((s) => MENU_SLOT_DEFS.some((x) => x.id === s));
  const items = menu?.items || {};
  const hasAnything = slots.some((s) => MENU_DAY_DEFS.some((d) => (items[s]?.[d.id] || []).length > 0));
  if (!loading && !hasAnything) return null;

  const weekFri = addDaysMenu(weekOf, 4);
  const yr = new Date(weekOf).getFullYear();
  const weekLabel = `${friendlyMenu(weekOf)} — ${friendlyMenu(weekFri)} ${yr}`;
  const weekOfFriendly = (() => {
    const [y, m, d] = weekOf.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  })();

  const goWeek = (n) => setWeekOf(addDaysMenu(weekOf, n * 7));

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden mb-6" data-testid="parent-food-menu">
      {/* Navy header */}
      <div className="bg-[#0F1B47] text-[#FDFDF9] px-6 md:px-9 py-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#5BC4B8]/20 text-[#5BC4B8] flex items-center justify-center flex-shrink-0">
          <Utensils size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#5BC4B8]">Weekly Menu</div>
          <div className="font-display text-2xl font-medium mt-1 leading-tight">This week's food menu</div>
          <div className="text-[13px] text-[#FDFDF9]/70 mt-1">Wholesome, vegetarian meals freshly prepared at school.</div>
        </div>
        <div className="hidden md:block text-right text-[11px] text-[#FDFDF9]/70 shrink-0">
          <div className="text-[#FDFDF9] font-semibold text-sm">Week of {weekOfFriendly}</div>
        </div>
      </div>

      {/* Pager */}
      <div className="bg-[#F5F5F0] px-6 md:px-9 py-3.5 flex items-center justify-between">
        <button onClick={() => goWeek(-1)} className="w-9 h-9 rounded-full bg-[#FDFDF9] border border-[#0F1B47]/10 text-[#0F1B47] hover:bg-[#0F1B47]/5 flex items-center justify-center" data-testid="parent-menu-prev">
          <ChevronRight size={16} className="rotate-180" />
        </button>
        <div className="font-display text-[16px] md:text-lg text-[#0F1B47] font-medium" data-testid="parent-menu-week-label">{weekLabel}</div>
        <button onClick={() => goWeek(1)} className="w-9 h-9 rounded-full bg-[#FDFDF9] border border-[#0F1B47]/10 text-[#0F1B47] hover:bg-[#0F1B47]/5 flex items-center justify-center" data-testid="parent-menu-next">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Days grid */}
      <div className="px-5 md:px-7 py-6">
        {loading ? (
          <div className="text-sm text-[#718096] py-8 text-center">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {MENU_DAY_DEFS.map((d, idx) => {
              const dayIso = addDaysMenu(weekOf, idx);
              const isToday = dayIso === todayIso;
              const dnum = new Date(dayIso).getDate();
              const dayHasItems = slots.some((s) => (items[s]?.[d.id] || []).length > 0);
              return (
                <div
                  key={d.id}
                  className={`rounded-2xl p-4 relative ${isToday ? "bg-[#FDFDF9] border-2 border-[#C6D947] shadow-lg" : "bg-[#F5F5F0] border border-[#0F1B47]/06"}`}
                  data-testid={`parent-menu-day-${d.id}`}
                >
                  {isToday && (
                    <span className="absolute top-3 right-3 bg-[#C6D947] text-[#0F1B47] text-[9px] font-extrabold tracking-[0.16em] uppercase rounded-full px-2 py-0.5">Today</span>
                  )}
                  <div className="flex items-baseline gap-1.5 mb-3">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">{d.label}</div>
                    <div className="font-display text-[22px] font-medium text-[#0F1B47] leading-none">{dnum}</div>
                  </div>
                  {dayHasItems ? (
                    slots.map((sid) => {
                      const slotDef = MENU_SLOT_DEFS.find((x) => x.id === sid);
                      const list = items[sid]?.[d.id] || [];
                      if (list.length === 0) return null;
                      return (
                        <div key={sid} className="mb-3 last:mb-0">
                          <div className="text-[9px] font-extrabold tracking-[0.18em] uppercase text-[#4A5568] mb-1.5 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: slotDef?.dot || "#648DE5" }} />
                            {slotDef?.label || sid}
                          </div>
                          <ul className="list-disc pl-4 text-[12.5px] leading-[1.55] text-[#0F1B47] space-y-0.5">
                            {list.map((it, i) => <li key={i}>{it}</li>)}
                          </ul>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-[11px] text-[#A0AEC0] italic">Menu not posted yet.</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const LEAVE_STATUS_TONE = {
  pending: "bg-[#C6D947]/30 text-[#0F1B47] border-[#C6D947]/60",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-red-50 text-red-700 border-red-200",
};

function SchoolCalendarPdfCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/school-calendar`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  const pdf = data?.pdf;
  if (!pdf) return null;

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden mb-6 mt-6" data-testid="parent-school-calendar-pdf">
      <div className="bg-[#0F1B47] text-[#FDFDF9] px-7 md:px-9 py-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#5BC4B8]/20 text-[#5BC4B8] flex items-center justify-center flex-shrink-0">
          <CalendarDays size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#5BC4B8]">School calendar</div>
          <div className="font-display text-2xl font-medium mt-1 leading-tight">
            {data?.title || "Annual school calendar"}
          </div>
          {data?.academic_year && (
            <div className="text-xs text-[#FDFDF9]/70 mt-1">Academic year {data.academic_year}</div>
          )}
        </div>
      </div>
      <div className="px-5 md:px-7 py-6">
        <div className="bg-[#F5F5F0] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#FDFDF9] text-[#5BC4B8] flex items-center justify-center flex-shrink-0">
            <FileText size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0F1B47] truncate">{pdf.filename || "School calendar"}</div>
            <div className="text-xs text-[#4A5568] mt-0.5">
              Uploaded {pdf.uploaded_at ? new Date(pdf.uploaded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}
            </div>
          </div>
          <a
            href={`${API}/school-calendar.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] shrink-0"
            data-testid="parent-school-calendar-download"
          >
            <Download size={14} /> Download calendar
          </a>
        </div>
        {data?.note && (
          <p className="mt-4 text-xs text-[#4A5568] font-light italic">{data.note}</p>
        )}
      </div>
    </div>
  );
}

function LeaveRequestCard({ students }) {
  const [history, setHistory] = useState([]);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await parentApi.get("/parent/leave-requests");
      setHistory(data || []);
    } catch {/* 401 handled globally */}
    finally { setLoadedOnce(true); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden mb-6" data-testid="parent-leave-card">
      <div className="bg-[#0F1B47] text-[#FDFDF9] px-7 md:px-9 py-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#C6D947]/20 text-[#C6D947] flex items-center justify-center flex-shrink-0">
          <MessageSquare size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C6D947]">Send a note</div>
          <div className="font-display text-2xl font-medium mt-1 leading-tight">Leave request</div>
          <p className="text-[12px] text-white/70 mt-1.5 max-w-lg">Let the school know in advance if your child will be away. The superadmin reviews and gets back to you.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="hidden sm:inline-flex items-center gap-2 bg-[#C6D947] text-[#0F1B47] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#d6e860] shrink-0"
          data-testid="parent-leave-new-btn"
        >
          <Send size={14} /> New request
        </button>
      </div>

      <div className="px-5 md:px-7 py-6">
        <button
          onClick={() => setShowForm(true)}
          className="sm:hidden mb-4 w-full inline-flex items-center justify-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm"
          data-testid="parent-leave-new-btn-mobile"
        >
          <Send size={14} /> New leave request
        </button>

        {!loadedOnce ? (
          <div className="text-sm text-[#718096] py-6 text-center">Loading…</div>
        ) : history.length === 0 ? (
          <div className="bg-[#F5F5F0] rounded-2xl p-8 text-center">
            <MessageSquare size={26} className="mx-auto text-[#648DE5] mb-2" />
            <div className="text-[#0F1B47] font-medium text-sm">No leave requests yet</div>
            <p className="text-xs text-[#718096] mt-1">Submit your first request whenever your child needs to be away.</p>
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="parent-leave-history">
            {history.map((r) => (
              <li key={r.id} className="bg-[#F5F5F0] rounded-2xl p-4" data-testid={`parent-leave-row-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-[#0F1B47]">{r.student_name}</div>
                  <span className={`text-[9px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 border ${LEAVE_STATUS_TONE[r.status] || LEAVE_STATUS_TONE.pending}`}>
                    {r.status}
                  </span>
                  <span className="text-[10px] text-[#718096] ml-auto">
                    Submitted {new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                  </span>
                </div>
                <div className="text-xs text-[#4A5568] mt-1.5">
                  <strong className="text-[#0F1B47]">{r.days} day{r.days === 1 ? "" : "s"}</strong> from {new Date(r.start_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <div className="text-[12px] text-[#0F1B47] mt-1 italic">"{r.reason}"</div>
                {r.admin_note && (
                  <div className="mt-2 pt-2 border-t border-[#0F1B47]/8 text-[12px] text-[#0F1B47]">
                    <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mr-1.5">School reply</span>
                    {r.admin_note}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm && (
        <LeaveRequestForm
          students={students}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function LeaveRequestForm({ students, onClose, onSubmitted }) {
  const [studentId, setStudentId] = useState(students[0]?.id || "");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [days, setDays] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!studentId) return toast.error("Pick a child");
    if (!startDate) return toast.error("Pick a start date");
    if (!days || days < 1) return toast.error("Days must be at least 1");
    if (!reason.trim() || reason.trim().length < 3) return toast.error("Add a short reason (3+ characters)");
    setSaving(true);
    try {
      await parentApi.post("/parent/leave-requests", {
        student_id: studentId,
        start_date: startDate,
        days: Number(days),
        reason: reason.trim(),
      });
      toast.success("Leave request sent. The school will review it shortly.");
      onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFDF9] rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
        data-testid="parent-leave-form"
      >
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Send a note</div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">New leave request</h3>
            <p className="text-xs text-[#4A5568] mt-0.5">The superadmin will review and reply on your portal.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          {students.length > 1 && (
            <div>
              <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">Child *</span>
              <select
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
                data-testid="parent-leave-student"
              >
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{s.full_name} · {s.class}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">Start date *</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
                data-testid="parent-leave-start-date"
              />
            </div>
            <div>
              <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">No. of days *</span>
              <input
                type="number"
                min="1"
                max="60"
                value={days}
                onChange={(e) => setDays(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
                data-testid="parent-leave-days"
              />
            </div>
          </div>
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">Reason for leave *</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={600}
              placeholder="e.g. Out of town for a family wedding"
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9] resize-none"
              data-testid="parent-leave-reason"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3 bg-[#FDFDF9]">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
            data-testid="parent-leave-submit"
          >
            <Send size={14} /> {saving ? "Sending…" : "Send request"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}



function NewsletterCard() {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/newsletters`)
      .then(({ data }) => { if (!cancelled) setList(data || []); })
      .catch(() => { if (!cancelled) setList([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || list.length === 0) return null;
  const latest = list[0];
  const earlier = list.slice(1, 4);
  const pdfBase = `${process.env.REACT_APP_BACKEND_URL}/api/newsletters`;

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden mb-6" data-testid="parent-newsletter">
      <div className="bg-[#648DE5] text-[#FDFDF9] px-7 md:px-9 py-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#FDFDF9]/15 text-[#FDFDF9] flex items-center justify-center flex-shrink-0">
          <Newspaper size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#FDFDF9]/85">Monthly newsletter</div>
          <div className="font-display text-2xl font-medium mt-1 leading-tight">{latest.title || latest.month_label}</div>
          <div className="text-xs text-[#FDFDF9]/75 mt-1">{latest.month_label}</div>
        </div>
      </div>
      <div className="px-7 py-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#648DE5]/10 text-[#648DE5] flex items-center justify-center flex-shrink-0">
            <FileText size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-[#0F1B47] truncate">{latest.filename}</div>
            <div className="text-[11px] text-[#718096]">Uploaded {new Date(latest.uploaded_at).toLocaleDateString()}</div>
          </div>
        </div>
        <a
          href={`${pdfBase}/${latest.id}.pdf`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
          data-testid="parent-newsletter-download"
        >
          <Download size={14} /> Read newsletter
        </a>
      </div>
      {earlier.length > 0 && (
        <div className="px-7 pb-6 pt-0">
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5] mb-2.5">Earlier issues</div>
          <ul className="space-y-2">
            {earlier.map((n) => (
              <li key={n.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="text-[#0F1B47] truncate">{n.title || n.month_label}</div>
                <a
                  href={`${pdfBase}/${n.id}.pdf`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#648DE5] hover:text-[#0F1B47]"
                  data-testid={`parent-newsletter-old-${n.id}`}
                >
                  <Download size={11} /> PDF
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Avatar({ url, name, size = 56 }) {
  if (url) {
    return <img src={url} alt={name} className="rounded-2xl object-cover bg-[#F5F5F0] flex-shrink-0" style={{ width: size, height: size }} loading="lazy" decoding="async" />;
  }
  const initials = (name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="rounded-2xl bg-[#E3EDF9] text-[#0F1B47] font-semibold flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.34 }}>
      {initials}
    </div>
  );
}

function AttStat({ label, value, cls }) {
  return (
    <div className={`rounded-2xl px-3 py-2 text-center ${cls}`}>
      <div className="text-[9px] font-bold tracking-[0.18em] uppercase opacity-80">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function SummaryStat({ label, value, tone }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700",
    teal: "bg-teal-50 text-teal-700",
    amber: "bg-amber-50 text-amber-700",
    green: "bg-emerald-50 text-emerald-700",
    neutral: "bg-[#F5F5F0] text-[#0F1B47]",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ${tones[tone] || tones.rose}`}>
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-80">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}
